import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Typography,
  Alert,
  Select,
  MenuItem,
  FormControl,
  alpha,
  useTheme,
  Fade,
  Paper
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import config from "../../../config";

const POSEditExcel = () => {
  const { store_id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  const [storeConfig, setStoreConfig] = useState(null);

  const [manualFile, setManualFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const [analyzing, setAnalyzing] = useState(false);
  const [mappingSuggestions, setMappingSuggestions] = useState([]);
  const [confirmedMappings, setConfirmedMappings] = useState([]);
  const [targetAttributes, setTargetAttributes] = useState([]);
  const [requiredMissing, setRequiredMissing] = useState([]);
  const [shopifyAutoFields, setShopifyAutoFields] = useState([]);

  const [activeStep, setActiveStep] = useState(1); // 1 = Upload, 2 = Map

  useEffect(() => {
    fetchStoreConfig();
  }, [store_id]);

  const fetchStoreConfig = async () => {
    try {
      const res = await fetch(`${config.API_BASE_URL}/client/pos/get-store-config/${store_id}`, {
        headers: config.getHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setStoreConfig(data.data);
      } else {
        setError("Failed to load store config");
      }
    } catch (err) {
      setError("Error loading store config");
    } finally {
      setInitialLoading(false);
    }
  };

  const processFile = (file) => {
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls') && !fileName.endsWith('.csv')) {
      setError("Invalid file type. Please upload .xlsx, .xls, or .csv");
      setManualFile(null);
      return;
    }
    setManualFile(file);
    setError(null);
  };

  const handleAnalyzeFile = async () => {
    if (!manualFile || !storeConfig) return;
    setAnalyzing(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("file", manualFile);

      const posSystem = storeConfig.pos_name || "Universal";
      const url = `${config.API_BASE_URL}/client/pos/analyze-excel?client_store_id=${store_id}&pos_system=${posSystem}`;

      const headers = config.getHeaders();
      delete headers["Content-Type"];

      const resp = await fetch(url, { method: "POST", headers, body: formData });
      const result = await resp.json();

      if (!resp.ok || !result.success) throw new Error(result.message || "Analysis failed");

      setMappingSuggestions(result.data.suggestions);
      setConfirmedMappings(result.data.suggestions.map((s) => ({ ...s })));
      setTargetAttributes(result.data.target_attributes || []);
      setRequiredMissing(result.data.required_missing || []);
      setShopifyAutoFields(result.data.shopify_auto_fields || []);

      setActiveStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleExcelMappingChange = (excelCol, newAttributeName) => {
    if (newAttributeName) {
      const alreadyUsed = confirmedMappings.some(
        (m) => m.excel_col !== excelCol && m.attribute_name === newAttributeName
      );
      if (alreadyUsed) {
        setError(`"${newAttributeName}" is already mapped. Bud-i attributes can only be used once.`);
        return;
      }
    }
    setError(null);

    const attr = targetAttributes.find((a) => a.attribute_name === newAttributeName);
    setConfirmedMappings((prev) =>
      prev.map((m) =>
        m.excel_col === excelCol
          ? {
              ...m,
              attribute_name: newAttributeName || null,
              product_attribute_master_id: attr?.product_attribute_master_id || null,
            }
          : m
      )
    );
  };

  const handleSaveMapping = async () => {
    setLoading(true);
    setError(null);
    try {
      const posSystem = storeConfig.pos_name || "Universal";

      const formData = new URLSearchParams();
      formData.append("pos_system", posSystem);
      formData.append("client_store_id", store_id);
      formData.append("confirmed_mappings", JSON.stringify(confirmedMappings));

      const resp = await fetch(`${config.API_BASE_URL}/client/pos/update-excel-mapping`, {
        method: "POST",
        headers: {
          ...config.getHeaders(),
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData.toString(),
      });

      const result = await resp.json();
      if (!resp.ok || !result.success) throw new Error(result.message || "Save failed");

      navigate("/pos-sync");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Calculate missing required attributes
  const mappedAttrs = new Set(confirmedMappings.map((m) => m.attribute_name).filter(Boolean));
  // filter target attributes ignoring shopifyAutoFields
  const effectiveTargets = targetAttributes.filter((a) => !shopifyAutoFields.includes(a.attribute_name));
  const reqMissing = effectiveTargets.filter((a) => a.required && !mappedAttrs.has(a.attribute_name));

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", mt: 4 }}>
      <Typography variant="h4" fontWeight={800} mb={1}>
        Update Field Mappings
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Store: <strong>{storeConfig?.store_name}</strong> | POS: <strong>{storeConfig?.pos_name}</strong>
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* STEP 1: UPLOAD SAMPLE EXCEL */}
      {activeStep === 1 && (
        <Fade in={true}>
          <Card
            sx={{
              p: { xs: 3, md: 6 },
              borderRadius: 4,
              boxShadow: "0 10px 40px -10px rgba(0,0,0,0.1)",
              textAlign: "center",
            }}
          >
            <Typography variant="h5" fontWeight={700} mb={2}>
              Upload New Data Sample
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={4}>
              Upload a recent export from your POS. We will analyze the headers to let you configure the new mapping. No products will be synced yet.
            </Typography>

            <Box
              sx={{
                border: "2px dashed",
                borderColor: isDragging ? "primary.main" : "divider",
                bgcolor: isDragging ? alpha(theme.palette.primary.main, 0.05) : "background.paper",
                borderRadius: 4,
                p: { xs: 4, md: 8 },
                mb: 4,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": { borderColor: "primary.main", bgcolor: alpha(theme.palette.primary.main, 0.02) },
              }}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                  processFile(e.dataTransfer.files[0]);
                }
              }}
            >
              <input
                type="file"
                id="file-upload"
                style={{ display: "none" }}
                onChange={(e) => e.target.files.length && processFile(e.target.files[0])}
                accept=".csv,.xlsx,.xls"
              />
              <label htmlFor="file-upload" style={{ cursor: "pointer", display: "block" }}>
                <CloudUploadIcon color={isDragging ? "primary" : "action"} sx={{ fontSize: 60, opacity: 0.8, mb: 2 }} />
                <Typography variant="h6" fontWeight={600}>
                  {manualFile ? manualFile.name : "Drag & Drop your Export"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {manualFile ? `${(manualFile.size / 1024).toFixed(1)} KB` : "or click to browse files (.xlsx, .csv)"}
                </Typography>
              </label>
            </Box>

            <Button
              variant="contained"
              size="large"
              onClick={handleAnalyzeFile}
              disabled={!manualFile || analyzing}
              sx={{ py: 1.5, px: 6, borderRadius: 2, fontWeight: 700 }}
            >
              {analyzing ? <CircularProgress size={24} color="inherit" /> : "Analyze Columns with AI"}
            </Button>
          </Card>
        </Fade>
      )}

      {/* STEP 2: MAPPING TABLE */}
      {activeStep === 2 && (
        <Fade in={true}>
          <Box>
            {reqMissing.length > 0 && (
              <Alert severity="warning" sx={{ mb: 4, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Missing Required Fields
                </Typography>
                <Typography variant="body2">
                  Please map the following required attributes before saving:{" "}
                  <strong>{reqMissing.map((r) => r.attribute_name).join(", ")}</strong>
                </Typography>
              </Alert>
            )}

            <Paper sx={{ borderRadius: 3, overflow: "hidden", border: "1px solid", borderColor: "divider", mb: 4 }}>
              {/* Header */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 80px",
                  gap: 2,
                  p: 2,
                  bgcolor: "grey.50",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                  Your Excel Column
                </Typography>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                  Map to Bud-e Attribute
                </Typography>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" textAlign="center">
                  AI Match
                </Typography>
              </Box>

              {/* Rows */}
              <Box sx={{ maxHeight: 600, overflowY: "auto" }}>
                {confirmedMappings.map((mapping, idx) => {
                  const conf = mapping.confidence || 0;
                  const isLocked = shopifyAutoFields.includes(mapping.attribute_name);
                  const isMapped = !!mapping.attribute_name;
                  const dotColor = conf > 0.8 ? theme.palette.success.main : conf > 0.5 ? theme.palette.warning.main : theme.palette.error.main;

                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 80px",
                        gap: 2,
                        p: 2,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        alignItems: "center",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <Typography fontWeight={isMapped ? 600 : 400} color={isMapped ? "text.primary" : "text.secondary"}>
                        {mapping.excel_col}
                      </Typography>

                      <FormControl size="small" fullWidth disabled={isLocked}>
                        <Select
                          value={mapping.attribute_name || ""}
                          onChange={(e) => handleExcelMappingChange(mapping.excel_col, e.target.value)}
                          displayEmpty
                          sx={{
                            bgcolor: isMapped ? "background.paper" : "transparent",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: isMapped ? "primary.light" : "divider",
                            },
                          }}
                        >
                          <MenuItem value="">
                            <Typography color="text.secondary">
                              <em>-- Do not map this column --</em>
                            </Typography>
                          </MenuItem>
                          {targetAttributes.map((attr) => (
                            <MenuItem key={attr.attribute_name} value={attr.attribute_name}>
                              {attr.attribute_name} {attr.required ? " * (Required)" : ""}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: isMapped ? dotColor : "grey.300",
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: isMapped ? "text.primary" : "text.disabled" }}>
                          {isMapped ? `${Math.round(conf * 100)}%` : "—"}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => {
                  setActiveStep(1);
                  setManualFile(null);
                }}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                Start Over
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={handleSaveMapping}
                disabled={loading || reqMissing.length > 0}
                sx={{ py: 1.5, px: 6, borderRadius: 2, fontWeight: 700 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Save New Mappings"}
              </Button>
            </Box>
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default POSEditExcel;
